/**
 * PriorReportPanel — Phase 3 RXF Comparison Reporting
 *
 * Shown in CurrentExaminationPanel when a session is active.
 * Auto-fetches prior report from patient history, or accepts manual upload.
 * Once attached, the pipeline injects comparison context automatically.
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Collapse,
  Divider,
  IconButton,
  Paper,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import CompareArrowsIcon from '@mui/icons-material/CompareArrows';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CloseIcon from '@mui/icons-material/Close';
import HistoryIcon from '@mui/icons-material/History';
import {
  autoFetchPriorReport,
  uploadPriorReport,
  deletePriorReport,
  attachPriorReportText,
  type PriorReportAttachment,
} from '../../services/rxfService';

interface PriorReportPanelProps {
  sessionId: string;
  /** Optional: patient selected — triggers auto-fetch */
  patientId?: number | string;
}

const PriorReportPanel: React.FC<PriorReportPanelProps> = ({ sessionId, patientId }) => {
  const [attachment, setAttachment] = useState<PriorReportAttachment | null>(null);
  const [autoFetching, setAutoFetching] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [showUpload, setShowUpload] = useState(false);
  const [pasteMode, setPasteMode] = useState(false);
  const [pasteText, setPasteText] = useState('');
  const [error, setError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Auto-fetch from patient history when patient changes
  useEffect(() => {
    if (!sessionId || !patientId) return;
    let cancelled = false;
    setAutoFetching(true);
    setError(null);
    autoFetchPriorReport(sessionId)
      .then((res) => {
        if (!cancelled) {
          setAttachment(res);
          setAutoFetching(false);
        }
      })
      .catch(() => {
        if (!cancelled) setAutoFetching(false);
      });
    return () => { cancelled = true; };
  }, [sessionId, patientId]);

  const handleFileChange = useCallback(
    async (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      if (!file) return;
      setUploading(true);
      setError(null);
      try {
        const att = await uploadPriorReport(sessionId, file);
        setAttachment(att);
        setShowUpload(false);
      } catch {
        setError('Upload failed — please try again or paste the report text.');
      } finally {
        setUploading(false);
        if (fileInputRef.current) fileInputRef.current.value = '';
      }
    },
    [sessionId],
  );

  const handlePasteSubmit = useCallback(async () => {
    if (!pasteText.trim()) return;
    setUploading(true);
    setError(null);
    try {
      const att = await attachPriorReportText(sessionId, pasteText);
      setAttachment(att);
      setPasteMode(false);
      setPasteText('');
    } catch {
      setError('Failed to attach prior report text.');
    } finally {
      setUploading(false);
    }
  }, [sessionId, pasteText]);

  const handleRemove = useCallback(async () => {
    if (!attachment) return;
    try {
      await deletePriorReport(attachment.id);
      setAttachment(null);
    } catch {
      setError('Failed to remove prior report.');
    }
  }, [attachment]);

  const statusColor =
    attachment?.extraction_status === 'done'
      ? 'success'
      : attachment?.extraction_status === 'failed'
        ? 'error'
        : 'warning';

  return (
    <Box sx={{ mt: 1.5 }}>
      {/* Header row */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 0.5 }}>
        <CompareArrowsIcon color="action" fontSize="small" />
        <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
          PRIOR REPORT COMPARISON
        </Typography>
      </Box>

      {autoFetching ? (
        <Stack direction="row" alignItems="center" spacing={1} sx={{ py: 0.5 }}>
          <CircularProgress size={14} />
          <Typography variant="caption" color="text.secondary">
            Checking patient history…
          </Typography>
        </Stack>
      ) : attachment ? (
        /* Attached state */
        <Paper
          variant="outlined"
          sx={{ p: 1.25, borderColor: `${statusColor}.main`, borderRadius: 1 }}
        >
          <Stack direction="row" alignItems="center" justifyContent="space-between">
            <Stack direction="row" alignItems="center" spacing={1}>
              {attachment.extraction_status === 'done' ? (
                <CheckCircleIcon color="success" fontSize="small" />
              ) : attachment.source === 'patient_history' ? (
                <HistoryIcon color="primary" fontSize="small" />
              ) : (
                <CircularProgress size={14} />
              )}
              <Box>
                <Typography variant="caption" sx={{ fontWeight: 600 }}>
                  {attachment.exam_type || attachment.original_filename || 'Prior Report'}
                </Typography>
                {attachment.exam_date && (
                  <Typography variant="caption" color="text.secondary" sx={{ ml: 0.5 }}>
                    · {attachment.exam_date}
                  </Typography>
                )}
              </Box>
              <Chip
                size="small"
                label={attachment.source === 'patient_history' ? 'Auto-fetched' : 'Uploaded'}
                variant="outlined"
                sx={{ height: 18, fontSize: '0.65rem' }}
              />
            </Stack>
            <Tooltip title="Remove prior report">
              <IconButton size="small" onClick={handleRemove}>
                <CloseIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Stack>
          {attachment.extraction_status === 'done' && (
            <Typography variant="caption" color="success.main" sx={{ display: 'block', mt: 0.5 }}>
              Comparison will be included in the generated report
            </Typography>
          )}
          {attachment.extraction_status === 'failed' && (
            <Typography variant="caption" color="error" sx={{ display: 'block', mt: 0.5 }}>
              Extraction failed — comparison will be skipped
            </Typography>
          )}
        </Paper>
      ) : (
        /* No attachment — show upload zone */
        <Box>
          {!showUpload && !pasteMode ? (
            <Stack direction="row" spacing={1}>
              <Button
                size="small"
                variant="outlined"
                startIcon={<UploadFileIcon />}
                onClick={() => setShowUpload(true)}
                sx={{ fontSize: '0.75rem' }}
              >
                Upload Prior Report
              </Button>
              <Button
                size="small"
                variant="text"
                onClick={() => setPasteMode(true)}
                sx={{ fontSize: '0.75rem' }}
              >
                Paste text
              </Button>
            </Stack>
          ) : null}

          <Collapse in={showUpload && !pasteMode}>
            <Box
              sx={{
                mt: 1,
                border: '1px dashed',
                borderColor: 'divider',
                borderRadius: 1,
                p: 2,
                textAlign: 'center',
                cursor: 'pointer',
              }}
              onClick={() => fileInputRef.current?.click()}
            >
              {uploading ? (
                <CircularProgress size={20} />
              ) : (
                <>
                  <UploadFileIcon color="action" />
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                    PDF, DOCX, TXT, or image · Click to browse
                  </Typography>
                </>
              )}
              <input
                ref={fileInputRef}
                type="file"
                hidden
                accept=".pdf,.docx,.doc,.txt,.png,.jpg,.jpeg"
                onChange={handleFileChange}
              />
            </Box>
            <Button size="small" sx={{ mt: 0.5 }} onClick={() => setShowUpload(false)}>
              Cancel
            </Button>
          </Collapse>

          <Collapse in={pasteMode}>
            <Box sx={{ mt: 1 }}>
              <textarea
                rows={6}
                placeholder="Paste prior report text here…"
                value={pasteText}
                onChange={(e) => setPasteText(e.target.value)}
                style={{
                  width: '100%',
                  fontFamily: 'inherit',
                  fontSize: '0.8rem',
                  padding: '8px',
                  borderRadius: 4,
                  borderColor: '#ccc',
                  resize: 'vertical',
                  boxSizing: 'border-box',
                }}
              />
              <Stack direction="row" spacing={1} sx={{ mt: 0.5 }}>
                <Button
                  size="small"
                  variant="contained"
                  onClick={handlePasteSubmit}
                  disabled={uploading || !pasteText.trim()}
                >
                  {uploading ? <CircularProgress size={14} /> : 'Attach'}
                </Button>
                <Button size="small" onClick={() => { setPasteMode(false); setPasteText(''); }}>
                  Cancel
                </Button>
              </Stack>
            </Box>
          </Collapse>
        </Box>
      )}

      {error && (
        <Alert severity="error" sx={{ mt: 1, py: 0.5 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Divider sx={{ mt: 1.5 }} />
    </Box>
  );
};

export default PriorReportPanel;
